import { useCallback, useEffect, useRef, useState } from "react";
import { CardSkeleton, Loading } from "@/components/Loading";
import { ErrorAlert } from "@/components/ErrorAlert";
import { Card, CardAction, CardHeader } from "@/components/ui/card";
import { Check, FileSpreadsheet, PencilLine, X } from "lucide-react";
import { DropZone } from "@/components/DropZone";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { screens } from "../screens";
import { Skeleton } from "@/components/ui/skeleton";
import type { Role } from "../../shared/api";
import { StatusBadge } from "@/components/StatusBadge";
import { UploadDialog } from "@/components/UploadDialog";
import { Button } from "@/components/ui/button";
import { ManualEntryDialog, type ManualTarget } from "@/components/ManualEntryDialog";
import { canWrite as mayWrite } from "@/lib/guard";
import type { IntegrityResponse, SourceBox, SourcesResponse, UploadResponse } from "../../shared/api";
import { formatNumber } from "../../shared/versions";
import { versionLabel } from "../../shared/upload";
import { BOX_NAMES, type BoxName } from "../../shared/sources";
import type { SourceType } from "../../shared/rows";
import { SourceTypeName } from "@/components/SourceTypeName";
import { uploadHref } from "@/components/UploadDetailScreen";
import { errorText, getIntegrity, getSources } from "@/lib/client";
import { navigate } from "@/lib/nav";
import { BOX_LABEL, formatDay } from "@/lib/upload";
import { BOX_ICON, FooterLine, boxBadge, boxDetail, boxTone, type BoxStatus } from "@/components/sources-shared";

/** Versiyonu olmayan kaynak tipleri (ui.md §11.1): "Yeni kayıt" yalnız yazma yetkisi olana. */
export const emptyTypes = (box: SourceBox) => box.sourceTypes.filter((t) => !box.files.some((f) => f.sourceType === t));

const ROW = "flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 outline-none hover:bg-muted/50 focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring [&:hover_.file]:underline";

export function BoxView({ name, box, onNew }: { name: BoxName; box: SourceBox & BoxStatus; onNew?: (t: SourceType, opener: HTMLElement) => void }) {
  const badge = boxBadge(box);
  const empty = emptyTypes(box);
  const Icon = BOX_ICON[name];
  return (
    <Card role="region" aria-labelledby={`box-${name}`} className="gap-0 overflow-clip p-0">
      <CardHeader className="grid-rows-[auto] gap-1 border-b px-4 py-3 [.border-b]:pb-3">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="size-4" /></span>
          <div className="min-w-0">
            <h2 id={`box-${name}`} className="text-base font-semibold">{BOX_LABEL[name]}</h2>
            <p className="text-xs text-muted-foreground">{box.sourceTypes.map((t, i) => <span key={t}>{i > 0 && " · "}<SourceTypeName code={t} /></span>)}</p>
          </div>
        </div>
        {badge && <CardAction><StatusBadge tone={boxTone(box)}>{badge}</StatusBadge></CardAction>}
      </CardHeader>
      {boxDetail(box) && <p className="border-b px-4 py-2 text-sm text-warning-foreground">{boxDetail(box)}</p>}
      {box.files.length > 0 && (
        <ul className="flex flex-col divide-y text-sm">
          {box.files.map((f) => (
            <li key={f.uploadId}>
              <a href={uploadHref(f.uploadId)} onClick={(e) => navigate(e, uploadHref(f.uploadId))} className={ROW}>
                {f.origin === "manual" ? <PencilLine aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" /> : <FileSpreadsheet aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />}
                <span className="file min-w-0 flex-1 truncate text-primary underline-offset-4">{f.origin === "manual" ? `${f.file} · ${f.uploadedBy}` : f.file}</span>
                {f.origin === "manual" && <UserAvatar name={f.uploadedBy} />}
                <span className="text-right tabular-nums text-muted-foreground">{formatDay(f.uploadedAt)} · {formatNumber(f.recordCount)} kayıt</span>
              </a>
            </li>
          ))}
        </ul>
      )}
      {empty.length > 0 && (
        <ul className={`flex flex-col divide-y text-sm ${box.files.length > 0 ? "border-t" : ""}`}>
          {empty.map((t) => (
            <li key={t} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
              <span className="text-muted-foreground"><SourceTypeName code={t} /> · Henüz versiyon yok</span>
              {onNew && <Button type="button" variant="outline" size="sm" onClick={(e) => onNew(t, e.currentTarget)}>Yeni kayıt</Button>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Yükleme başarı bildirimi (#292): success tonu, ✓, kapatılabilir, yükleme ayrıntısına bağlantı. */
export function UploadNotice({ uploadId, text, onClose }: { uploadId: number; text: string; onClose: () => void }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-success-border bg-success px-4 py-3 text-sm text-success-foreground">
      <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <p role="status" className="min-w-0 flex-1">{text}</p>
      <a href={uploadHref(uploadId)} onClick={(e) => navigate(e, uploadHref(uploadId))} className="shrink-0 font-medium underline underline-offset-4">{versionLabel("file", uploadId)} ayrıntısı</a>
      <button type="button" aria-label="Bildirimi kapat" onClick={onClose} className="flex size-6 shrink-0 items-center justify-center rounded-xs opacity-70 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"><X aria-hidden="true" className="size-4" /></button>
    </div>
  );
}

/** E2 (spec §6). `role` yalnız düğmeyi gösterip göstermemeye karar verir; yetkiyi API denetler (ADR-0008). */
export function SourcesScreen({ role }: { role: Role }) {
  const [sources, setSources] = useState<SourcesResponse | null>(null);
  const [integrity, setIntegrity] = useState<IntegrityResponse | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{ uploadId: number; text: string } | null>(null);
  const [dropped, setDropped] = useState<File | null>(null);

  const load = useCallback(() => {
    setError("");
    getSources().then(setSources, (e) => setError(errorText(e)));
    getIntegrity().then(setIntegrity, () => setIntegrity(null));
  }, []);
  useEffect(load, [load]);

  const canWrite = mayWrite(role);
  const [manual, setManual] = useState<ManualTarget | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => { if (!manual) { opener.current?.focus(); opener.current = null; } }, [manual]); // the dialog is gone: focus goes back to the button that opened it
  // Bırakma alanı dışına düşen dosyayı tarayıcı açmasın (uygulamadan çıkılmasın).
  useEffect(() => {
    const stop = (e: Event) => e.preventDefault();
    window.addEventListener("dragover", stop); window.addEventListener("drop", stop);
    return () => { window.removeEventListener("dragover", stop); window.removeEventListener("drop", stop); };
  }, []);
  const boxes = sources && BOX_NAMES.map((id) => [id, sources.boxes[id]] as const);

  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Veri Kaynakları"
        description={screens.find((x) => x.id === "E2")!.description}
        actions={canWrite && <UploadDialog droppedFile={dropped} onDroppedFile={() => setDropped(null)} onUploaded={(r: UploadResponse) => { setNotice({ uploadId: r.uploadId, text: `${versionLabel("file", r.uploadId)}: ${formatNumber(r.rowCount)} kayıt (${formatNumber(r.newCount)} yeni, ${formatNumber(r.changedCount)} değişen, ${formatNumber(r.removedCount)} kaldırılan).` }); load(); }} />}
      />
      {notice && <UploadNotice {...notice} onClose={() => setNotice(null)} />}
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {!sources && !error && (
        <div aria-busy="true" className="flex flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-2"><CardSkeleton /><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
          <Loading><Skeleton className="h-5 w-72" /></Loading>
        </div>
      )}
      {boxes && (
        <>
          {canWrite && boxes.every(([, b]) => b.files.length === 0) && (
            <DropZone onFile={setDropped}>
              <p className="rounded-lg border-2 border-dashed p-4 text-center text-sm text-muted-foreground">Henüz dosya yüklenmedi. Excel dosyasını (.xlsx) buraya bırakın ya da “Dosya yükle”yi kullanın.</p>
            </DropZone>
          )}
          <div className="grid gap-4 md:grid-cols-2">{boxes.map(([id, b]) => <BoxView key={id} name={id} box={b} onNew={canWrite ? (t, el) => { opener.current = el; setManual({ sourceType: t, basedOn: null }); } : undefined} />)}</div>
          <FooterLine total={sources.totalRecords} integrity={integrity} empty={boxes.every(([, b]) => b.files.length === 0)} />
        </>
      )}
      {manual && <ManualEntryDialog target={manual} onClose={() => setManual(null)} />}
    </main>
  );
}
